import type * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { FormatNumber, formatToNumber } from "../../utils/formatNumber";

type NumericType = "integer" | "float";
type Formatter = (value: number) => string;

/**
 * Returns true for characters used as thousands separators by sv/fi
 * Intl.NumberFormat (regular space, non-breaking space, apostrophe).
 */
function isSep(char: string): boolean {
  return char === " " || char === "\u00A0" || char === "'";
}

/**
 * Count non-separator chars in `str` up to (but not including) `upTo`.
 * Used to remember the logical cursor position before reformatting.
 */
function countNonSep(str: string, upTo: number): number {
  let count = 0;
  for (let i = 0; i < upTo && i < str.length; i++) {
    const char = str[i];
    if (char && !isSep(char)) count++;
  }
  return count;
}

/**
 * Find the index just AFTER the n-th non-separator character in `str`.
 * Used to restore the cursor to the same logical position after reformatting.
 */
function posAfterNonSep(str: string, n: number): number {
  if (n === 0) return 0;
  let count = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str[i];
    if (char && !isSep(char)) {
      count++;
      if (count === n) return i + 1;
    }
  }
  return str.length;
}

/**
 * Format a float input string with live thousands separators while preserving
 * in-progress decimal entry (e.g. "1234," or "1234,5").
 * Returns `{ formatted, parsed }`.
 */
function formatFloatLive(
  raw: string,
  clean: string,
  format: Formatter,
): { formatted: string; parsed: number | null } {
  // Trailing decimal separator — user is mid-way: "1 234," or "1 234."
  const trailingDec = clean.match(/^(\d*)[,.]$/);
  // Partial decimal — has fractional digits: "1234,56"
  const partialDec = clean.match(/^(\d*)[,.](\d+)$/);

  if (trailingDec) {
    const intParsed = formatToNumber(trailingDec[1] ?? "", "integer");
    const intFormatted =
      intParsed != null ? format(intParsed) : (trailingDec[1] ?? "");
    return { formatted: `${intFormatted},`, parsed: intParsed };
  }

  if (partialDec) {
    const parsed = formatToNumber(raw, "float");
    const intParsed = formatToNumber(partialDec[1] ?? "0", "integer");
    const intFormatted =
      intParsed != null ? format(intParsed) : (partialDec[1] ?? "");
    return { formatted: `${intFormatted},${partialDec[2]}`, parsed };
  }

  const parsed = formatToNumber(raw, "float");
  return {
    formatted: parsed != null ? format(parsed) : clean === "" ? "" : raw,
    parsed,
  };
}

/**
 * Format an integer input string with live thousands separators.
 */
function formatIntegerLive(
  raw: string,
  clean: string,
  format: Formatter,
): { formatted: string; parsed: number | null } {
  const parsed = formatToNumber(raw, "integer");
  return {
    formatted: parsed != null ? format(parsed) : clean === "" ? "" : raw,
    parsed,
  };
}

interface IUseNumericInputOptions {
  value?: number | null;
  onChange?: (
    value: number | null,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onBlur?: (
    value: number | null,
    event: React.FocusEvent<HTMLInputElement>,
  ) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  type?: NumericType;
  /** Override the display formatter. Defaults to FormatNumber (sv locale). */
  format?: Formatter;
}

/**
 * Drives numeric inputs (integer, float, money, percentage).
 *
 * Thousands separators are inserted live on every keystroke while the cursor
 * position is preserved via a digit-counting algorithm. The raw string is
 * maintained in local state during editing so that:
 *  - Intermediate decimal states like "1 234," are never overwritten.
 *  - Cursor never jumps because position is explicitly restored after each format.
 *
 * On blur the local state is cleared and the formatted display value takes over.
 */
export function useNumericInput({
  value,
  onChange,
  onBlur,
  onFocus,
  type = "integer",
  format = FormatNumber,
}: IUseNumericInputOptions) {
  // undefined → not editing, show formatted display
  // string    → editing, show this live-formatted string as-is
  const [localValue, setLocalValue] = useState<string | undefined>(undefined);

  const formattedDisplay =
    value != null && value !== 0 ? format(value) : value === 0 ? "0" : "";

  const displayValue = localValue !== undefined ? localValue : formattedDisplay;

  // When an external source (e.g. a slider) changes the value while the input
  // is focused, localValue goes stale because only user keystrokes update it.
  // Sync localValue whenever formattedDisplay changes AND the user hasn't typed
  // anything different (localValue still equals what it was on the last render
  // with the old formattedDisplay).
  const prevFormattedDisplayRef = useRef(formattedDisplay);
  // localValue intentionally omitted from deps: we only want to sync when the
  // external value changes, not on every keystroke.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional
  useEffect(() => {
    const prev = prevFormattedDisplayRef.current;
    prevFormattedDisplayRef.current = formattedDisplay;
    if (
      localValue !== undefined &&
      localValue === prev &&
      formattedDisplay !== prev
    ) {
      setLocalValue(formattedDisplay);
    }
  }, [formattedDisplay]);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setLocalValue(formattedDisplay);
      onFocus?.(e);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formattedDisplay, onFocus],
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const raw = input.value;
      const cursorPos = input.selectionStart ?? raw.length;

      // Remember the logical cursor position (ignoring separator chars) so we
      // can restore it correctly after inserting/removing thousand separators.
      const nonSepBefore = countNonSep(raw, cursorPos);
      const clean = raw.replace(/[ \u00A0']/g, "");

      const { formatted, parsed } =
        type === "float"
          ? formatFloatLive(raw, clean, format)
          : formatIntegerLive(raw, clean, format);

      setLocalValue(formatted);
      onChange?.(parsed, e);

      // Restore cursor to the same logical position after React flushes the DOM.
      const newPos = posAfterNonSep(formatted, nonSepBefore);
      requestAnimationFrame(() => {
        if (input.isConnected) {
          input.setSelectionRange(newPos, newPos);
        }
      });
    },
    [onChange, type, format],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const parsed = formatToNumber(e.target.value, type);
      // Exit editing mode → formatted display takes over on next render.
      setLocalValue(undefined);
      onBlur?.(parsed, e);
    },
    [onBlur, type],
  );

  return { displayValue, handleFocus, handleChange, handleBlur };
}
